import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  addDaysToBusinessDate,
  type OverallReportDto,
  type PaymentsByEmployeeRowDto,
  type ProductCategoryDto,
  type ReportExportFormat,
  type ReportId,
  type ReportRange,
  type ReportTableDto,
} from '@gc/shared';

import {
  announceIfVenueSuspended,
  api,
  ApiError,
  currentSession,
  freshAccessToken,
} from '@/lib/api';
import type { CategoryOption } from './categorySelection';

/**
 * Reports data access (SPEC §5.7).
 *
 * The client never decides which business day a figure belongs to: it sends the
 * From/To instants its pickers produced and the server resolves them against
 * the venue's Opening Time. That is the whole reason a 02:00 sale reports on
 * the previous day consistently across the grid, the reports and the exports.
 */

export interface ReportRangeState {
  /** `YYYY-MM-DDTHH:mm`, venue-local, as `<input type="datetime-local">` produces. */
  from: string;
  to: string;
}

/** The venue's business day written out: Opening Time through next Closing Time. */
export function rangeFromBusinessDay(
  businessDate: string,
  openingTime: string,
  closingTime: string,
): ReportRangeState {
  return {
    from: `${businessDate}T${openingTime.slice(0, 5)}`,
    to: `${addDaysToBusinessDate(businessDate, 1)}T${closingTime.slice(0, 5)}`,
  };
}

/** The presets offered above the From/To pickers. */
export type ReportPreset = 'today' | 'yesterday' | 'last7' | 'thisMonth' | 'allTime';

/**
 * A preset written out as the two instants the pickers hold.
 *
 * Every preset ENDS at the current business day's close, so "last 7 days" is
 * seven business days rather than seven midnights — the venue trades past
 * midnight and a calendar week would cut each of those nights in half.
 *
 * `allTime` starts at the venue's first business date, which is what lets the
 * previous system's period totals into the report: they are all-or-nothing
 * against the period they cover, so a range that stops short of their start
 * excludes them entirely.
 */
export function rangeFromPreset(
  preset: ReportPreset,
  businessDate: string,
  openingTime: string,
  closingTime: string,
  earliestBusinessDate?: string | null,
): ReportRangeState {
  const today = rangeFromBusinessDay(businessDate, openingTime, closingTime);
  const startingOn = (from: string) => ({
    from: `${from}T${openingTime.slice(0, 5)}`,
    to: today.to,
  });

  switch (preset) {
    case 'today':
      return today;
    case 'yesterday': {
      const previous = addDaysToBusinessDate(businessDate, -1);
      return rangeFromBusinessDay(previous, openingTime, closingTime);
    }
    case 'last7':
      return startingOn(addDaysToBusinessDate(businessDate, -6));
    case 'thisMonth':
      return startingOn(`${businessDate.slice(0, 7)}-01`);
    case 'allTime':
      // No earliest known (an empty venue): fall back to today rather than
      // inventing a start date the figures would not justify.
      return earliestBusinessDate ? startingOn(earliestBusinessDate) : today;
  }
}

/** Which preset a range currently equals, so the chip can show as selected. */
export function matchPreset(
  range: ReportRangeState,
  catalogue: ReportCatalogue,
): ReportPreset | null {
  for (const preset of ['today', 'yesterday', 'last7', 'thisMonth', 'allTime'] as ReportPreset[]) {
    const candidate = rangeFromPreset(
      preset,
      catalogue.range.fromBusinessDate,
      catalogue.range.openingTime,
      catalogue.range.closingTime,
      catalogue.earliestBusinessDate,
    );
    if (candidate.from === range.from && candidate.to === range.to) return preset;
  }
  return null;
}

/**
 * The request's query string. `categoryIds` rides as one comma list — the
 * server accepts that or repeated params — and is left off when empty, which
 * the server reads as every category.
 */
export function rangeQuery(range: ReportRangeState, categoryIds?: readonly string[]): string {
  const params = new URLSearchParams();
  const from = new Date(range.from);
  const to = new Date(range.to);
  if (!Number.isNaN(from.getTime())) params.set('from', from.toISOString());
  if (!Number.isNaN(to.getTime())) params.set('to', to.toISOString());
  if (categoryIds && categoryIds.length > 0) params.set('categoryIds', categoryIds.join(','));
  return params.toString();
}

export interface ReportCatalogue {
  sections: Array<{ section: string; reports: Array<{ id: ReportId; title: string }> }>;
  range: ReportRange;
  /** First business date with any figures, live or archived. Null when empty. */
  earliestBusinessDate?: string | null;
}

/** The report list and the venue's default window, in one request. */
export function useReportCatalogue() {
  const [catalogue, setCatalogue] = useState<ReportCatalogue | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const response = await api.get<ReportCatalogue>('/reports/catalogue');
        if (!cancelled) setCatalogue(response);
      } catch (caught) {
        if (!cancelled) {
          setError(caught instanceof ApiError ? caught.message : 'Could not load reports.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { catalogue, error };
}

/**
 * One live report. `categoryIds` narrows the reports that honour it
 * (`showsCategoryFilter`); undefined is every category.
 */
export function useReportTable(
  id: ReportId | null,
  range: ReportRangeState | null,
  categoryIds?: readonly string[],
) {
  const [table, setTable] = useState<ReportTableDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const requestId = useRef(0);
  // A string, so a new array holding the same ids does not refetch.
  const categoryKey = categoryIds?.join(',') ?? '';

  const load = useCallback(async () => {
    if (!id || !range) {
      // Retire any request still in flight too — the category picker switches
      // the fetch off while nothing is ticked, and a late answer for the old
      // selection must not be what shows when the next tick lands.
      requestId.current += 1;
      setTable(null);
      setLoading(false);
      return;
    }
    const token = ++requestId.current;
    setLoading(true);
    try {
      const response = await api.get<ReportTableDto>(
        `/reports/${id}?${rangeQuery(range, categoryKey ? categoryKey.split(',') : undefined)}`,
      );
      if (token === requestId.current) {
        setTable(response);
        setError(null);
      }
    } catch (caught) {
      if (token === requestId.current) {
        setTable(null);
        setError(caught instanceof ApiError ? caught.message : 'Could not load the report.');
      }
    } finally {
      if (token === requestId.current) setLoading(false);
    }
  }, [id, range?.from, range?.to, categoryKey]);

  useEffect(() => {
    void load();
  }, [load]);

  return { table, loading, error, reload: load };
}

/**
 * The venue's product categories, for the category picker — menu order, as
 * the cafe screen lists them. `categories` stays null until they arrive, so the
 * picker can tell "still loading" from "this venue has none".
 */
export function useReportCategoryOptions(enabled: boolean) {
  const [categories, setCategories] = useState<CategoryOption[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    void (async () => {
      try {
        const response = await api.get<ProductCategoryDto[]>('/product-categories');
        if (!cancelled) {
          setCategories(response.map((category) => ({ id: category.id, name: category.name })));
          setError(null);
        }
      } catch (caught) {
        if (!cancelled) {
          setError(caught instanceof ApiError ? caught.message : 'Could not load categories.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { categories, error };
}

export function useOverallReport(range: ReportRangeState | null) {
  const [report, setReport] = useState<OverallReportDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const requestId = useRef(0);

  const load = useCallback(async () => {
    if (!range) return;
    const token = ++requestId.current;
    setLoading(true);
    try {
      const response = await api.get<OverallReportDto>(`/reports/overall?${rangeQuery(range)}`);
      if (token === requestId.current) {
        setReport(response);
        setError(null);
      }
    } catch (caught) {
      if (token === requestId.current) {
        setError(caught instanceof ApiError ? caught.message : 'Could not load the report.');
      }
    } finally {
      if (token === requestId.current) setLoading(false);
    }
  }, [range?.from, range?.to]);

  useEffect(() => {
    void load();
  }, [load]);

  return { report, loading, error, reload: load };
}

/**
 * The Payments-taken-by-employee request for one window (owner, 2026-10-12):
 * the same From/To instants every report sends. The server reads them against
 * `Payment.createdAt` — when the money was taken — not against business days.
 */
export function paymentsByEmployeePath(range: ReportRangeState): string {
  return `/reports/payments-by-employee?${rangeQuery(range)}`;
}

/**
 * Payments taken by employee for one window. A failed read keeps an ERROR and
 * drops the rows: a table left standing under a new window would pass the old
 * figures off as the new ones.
 */
export function usePaymentsByEmployee(range: ReportRangeState | null) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<PaymentsByEmployeeRowDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const requestId = useRef(0);

  const load = useCallback(async () => {
    if (!range) return;
    const token = ++requestId.current;
    setLoading(true);
    try {
      const response = await api.get<PaymentsByEmployeeRowDto[]>(paymentsByEmployeePath(range));
      if (token === requestId.current) {
        setRows(response);
        setError(null);
      }
    } catch (caught) {
      if (token === requestId.current) {
        setRows(null);
        setError(
          caught instanceof ApiError && caught.message
            ? caught.message
            : t('reports.paymentsByEmployee.loadFailed'),
        );
      }
    } finally {
      if (token === requestId.current) setLoading(false);
    }
  }, [range?.from, range?.to, t]);

  useEffect(() => {
    void load();
  }, [load]);

  return { rows, loading, error, reload: load };
}

/**
 * Download an export.
 *
 * Fetched rather than opened as a link because the access token lives in memory
 * only (see `lib/api.ts`) — a plain `<a href>` would arrive unauthenticated,
 * and putting the token in the URL would leak it into history and server logs.
 *
 * `freshAccessToken`, not the token as held: this fetch bypasses `api`, and so
 * its renew-and-retry. An owner who reads a report, leaves the screen, and
 * exports after the token lapsed got "Could not export" on the first click.
 *
 * Bypassing `api` also skipped its suspended-venue announcement: a fresh
 * token's export at a suspended venue said only "Could not export" (EclipseGG
 * platform 2 review, 2026-10-20). The server's code is now kept on the error,
 * and a 403 `venue_suspended` parks the app as every JSON request's does,
 * with the session it was sent in, so one that outlived a sign-out parks
 * nobody. The words under the button stay the export's own.
 */
export async function downloadReportExport(
  path: string,
  range: ReportRangeState,
  format: ReportExportFormat,
  categoryIds?: readonly string[],
): Promise<void> {
  const sentIn = currentSession();
  const token = await freshAccessToken();
  const response = await fetch(`/api${path}?${rangeQuery(range, categoryIds)}&format=${format}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include',
  });

  if (!response.ok) {
    const failure = new ApiError(
      response.status,
      (await errorCodeOf(response)) ?? 'export_failed',
      'Could not export this report.',
    );
    announceIfVenueSuspended(failure, sentIn);
    throw failure;
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const filename =
    /filename="([^"]+)"/.exec(response.headers.get('Content-Disposition') ?? '')?.[1] ??
    `report.${format}`;

  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  // Revoking immediately can cancel the download in some browsers; one tick is
  // enough for the click to have been handed off.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

/** A failed answer's `code`, when its body is the API's JSON error; null for anything else (a proxy's page, no body). */
async function errorCodeOf(response: Response): Promise<string | null> {
  try {
    const payload = (await response.json()) as { code?: unknown } | null;
    return typeof payload?.code === 'string' ? payload.code : null;
  } catch {
    return null;
  }
}
